/* ============================ WORKLOAD DETAIL + MONITORING PANEL ============================ */
import { useEffect, useState, type ReactNode } from "react";
import { DB, api, can, fire, go, job, nodeOf, rerender, siteNameOf, stackById, ui, wl } from "../lib/store";
import { gb, inFuture, lvl, when } from "../lib/fmt";
import { Badge, Bars, Sparkline } from "../ui/common";
import { Modal } from "../ui/modal";
import { TermBox, VncBox } from "../ui/term";
import { PAGES, type PageProps } from "./registry";
import { RowMenu } from "./fleet";
import { JobTable } from "./ops";
import { deployStack, importExisting, installSoftware } from "../modals/deploy";
import { deleteItem, drift, editTags, logs, resize, restore, runPlaybook, snapshot, tunnel, versions } from "../modals/ops";
import { addBackup } from "../modals/account";

const TAB_LABELS: Record<string, string> = {
  overview: "Overview", containers: "Containers & stacks", monitoring: "Monitoring", tasks: "Tasks", backups: "Backups", console: "Console",
};

function Overview({ w }: { w: any }) {
  const n = nodeOf(w);
  const op = can("Operator"), ad = can("Admin");
  const sw = w.software || [];
  const pub = w.public || {};
  const easy = w.type === "lxc" || w.guest_agent;
  return (
    <>
      {!w.ssh_ok && w.status === "running" ? (
        <div className="banner acc"><span>◈</span>
          <div><b>The console cannot log in to this machine yet</b>Docker, stacks, container metrics and playbooks need SSH access. {easy ? "It can be installed from the Proxmox host in one click." : "Import it with a user and password to install the console key."}</div>
          <div className="spacer" />
          {ad ? (easy
            ? <button className="btn sm primary" onClick={() => fire(job("POST", `/api/workloads/${w.id}/enable-management`))}>Enable management</button>
            : <button className="btn sm" onClick={() => importExisting(w.node || null, w.ip, w.name)}>Import with SSH</button>) : null}
        </div>
      ) : null}
      <div className="grid g2"><div className="card"><div className="hd"><h3>Configuration</h3><div className="spacer" />
        {op && !w.external ? <button className="btn sm" onClick={() => resize(w.id)}>Change resources</button> : null}
        {op ? <button className="btn sm ghost" onClick={() => editTags(w.id)}>Owner & tags</button> : null}</div><div className="bd">
        <dl className="kv"><dt>Type</dt><dd>{w.external ? "Imported machine" : String(w.type).toUpperCase() + " · vmid " + w.vmid}</dd><dt>Hostname</dt><dd>{w.host}</dd><dt>IP address</dt><dd>{w.ip}</dd>
          {w.mgmt_ip ? <><dt>Overlay IP</dt><dd>{w.mgmt_ip}</dd></> : null}
          <dt>Operating system</dt><dd>{w.os}</dd><dt>vCPU</dt><dd>{w.cores}</dd><dt>Memory</dt><dd>{w.mem} MB</dd><dt>Disk</dt><dd>{w.dsize} GB</dd>
          <dt>Edge device</dt><dd>{n.name}</dd><dt>Site</dt><dd>{siteNameOf(w)}</dd><dt>Owner</dt><dd>{w.owner}</dd><dt>Uptime</dt><dd>{w.uptimeTxt}</dd>
          <dt>SSH access</dt><dd>{w.ssh_ok ? "yes, as " + w.ssh_user : "no"}</dd><dt>Snapshots</dt><dd>{w.snapshots}</dd></dl></div></div>
        <div className="card"><div className="hd"><h3>Installed software</h3></div><div className="bd">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {w.docker ? <><span className="badge acc">Docker Engine {w.docker_version}</span><span className="badge acc">Compose v2</span></>
              : <span className="badge">{w.ssh_ok ? "No container runtime" : "unknown (no access)"}</span>}
            {w.k8s ? <span className="badge acc">k3s {w.k8s_version}</span> : null}
            {sw.map((s: any, i: number) => <span key={i} className="badge ok">{s}</span>)}
            {w.guest_agent ? <span className="badge ok">QEMU guest agent</span> : null}</div>
          <div className="btnrow">
            {op ? (w.docker ? <button className="btn primary" onClick={() => deployStack(w.id, "compose")}>Deploy compose stack</button>
              : <button className="btn primary" onClick={() => installSoftware(w.id)}>Install Docker</button>) : null}
            {op && w.k8s ? <button className="btn" onClick={() => deployStack(w.id, "k8s")}>Apply manifest</button> : null}
            {op && w.docker ? <button className="btn" onClick={() => installSoftware(w.id)}>Install more…</button> : null}
            {op ? <button className="btn" onClick={() => runPlaybook(`Workload: ${w.name}`)}>Run playbook</button> : null}</div>
          {w.drift.length ? (
            <div className="banner" style={{ margin: "16px 0 0" }}><span>△</span>
              <div><b>Drifted from saved state</b>{w.drift.map((d: any) => d.setting).join(", ")} differ from what this console last applied.</div>
              <div className="spacer" /><button className="btn sm" onClick={() => drift(w.id)}>Review</button></div>
          ) : null}
        </div></div></div>
      <div className="card" style={{ marginTop: 14 }}><div className="hd"><h3>Public access</h3></div><div className="bd">
        <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Expose a service on a public HTTPS URL through Tailscale Funnel. No firewall change is needed at the site.</p>
        <div style={{ display: "flex", gap: 9, alignItems: "center", flexWrap: "wrap" }}>
          {pub.url ? <code style={{ background: "var(--surface-2)", border: "1px solid var(--line)", padding: "6px 9px", borderRadius: 6 }}>{pub.url}</code> : <span className="muted">not published</span>}
          <span className={"badge " + (pub.enabled ? "ok" : "")}><span className="dot" />{pub.pending ? "updating…" : pub.enabled ? "active · port " + (pub.port || "?") : "not published"}</span>
          {pub.url && pub.enabled ? <a className="btn sm" href={pub.url} target="_blank" rel="noopener">Open</a> : null}
          {op ? <button className="btn sm" onClick={() => tunnel(w.id)}>Manage</button> : null}</div></div></div>
    </>
  );
}

function Containers({ w }: { w: any }) {
  const op = can("Operator"), ad = can("Admin");
  const stacks = DB.stacks.filter((s: any) => s.workload === w.id);
  const ctrs = DB.containers.filter((c: any) => c.workload === w.id);
  if (!ctrs.length && !stacks.length)
    return (
      <div className="empty"><h3>No containers here yet</h3>
        <p>{w.docker ? "Deploy a compose stack or pick something from the app catalog." : w.ssh_ok ? "Install Docker on this machine first — it takes about two minutes." : "The console needs SSH access before it can see or run containers here."}</p>
        <div className="btnrow" style={{ justifyContent: "center" }}>
          {op ? (w.docker ? <><button className="btn primary" onClick={() => deployStack(w.id, "compose")}>Deploy compose stack</button><button className="btn" onClick={() => go("catalog")}>Browse app catalog</button></>
            : <button className="btn primary" onClick={() => installSoftware(w.id)}>Install Docker</button>) : null}</div></div>
    );
  return (
    <>
      <div className="tbl-wrap"><table><thead><tr><th>Container</th><th>Stack</th><th>Image</th><th>Status</th><th>Ports</th><th>CPU / memory</th><th>Restarts</th><th></th></tr></thead><tbody>
        {ctrs.length ? ctrs.map((c: any) => (
          <tr key={c.id} className="row"><td><div className="tname"><span className="type-badge type-docker">Container</span><a onClick={() => logs(c.id)}>{c.name}</a></div></td>
            <td>{c.stack ? stackById(c.stack)?.name || "" : "—"}</td><td className="mono" style={{ fontSize: 12 }}>{c.image}</td>
            <td><Badge s={c.status} /></td><td className="mono" style={{ fontSize: 12 }}>{c.ports}</td>
            <td style={{ width: 150 }}><Bars w={{ cpu: c.cpu, ram: c.ram, disk: 0 }} /></td><td>{c.restarts > 3 ? <span className="badge bad">{c.restarts}</span> : c.restarts}</td>
            <td style={{ textAlign: "right" }}><RowMenu kind="container" id={c.id} /></td></tr>
        )) : <tr><td colSpan={8} className="muted">No containers.</td></tr>}</tbody></table></div>
      <div className="section-title">Stacks on this machine</div>
      <div className="grid g3">{stacks.length ? stacks.map((s: any) => (
        <div key={s.id} className="card"><div className="bd">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span className={"type-badge type-" + (s.kind === "k8s" ? "k8s" : "docker")}>{s.kind === "k8s" ? "Kubernetes" : "Compose"}</span><Badge s={s.status} /></div>
          <b style={{ display: "block", margin: "8px 0 2px" }}>{s.name}</b><div style={{ fontSize: 12.5, color: "var(--ink-2)" }}>{s.services} services · {s.version ? "version " + s.version : "found on host"} · updated {s.updatedTxt}</div>
          <div className="btnrow" style={{ marginTop: 11 }}>
            {op ? <button className="btn sm" onClick={() => deployStack(s.workload, s.kind, s.id)}>Edit</button> : null}
            <button className="btn sm" onClick={() => versions(s.id)}>History</button>
            {ad ? <button className="btn sm" onClick={() => deleteItem("stack", s.id)}>Delete</button> : null}</div></div></div>
      )) : <p className="muted">No stacks yet.</p>}</div>
    </>
  );
}

type Loaded = { list?: any[]; err?: string } | null;
const Loading = () => <div className="empty" style={{ padding: 20 }}><span className="spin" /></div>;

function StorageLists({ w }: { w: any }) {
  const op = can("Operator"), ad = can("Admin");
  const [bk, setBk] = useState<Loaded>(null);
  const [snaps, setSnaps] = useState<Loaded>(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try { const bs = await api("GET", `/api/workloads/${w.id}/backups`); if (alive) setBk({ list: bs }); }
      catch (e: any) { if (alive) setBk({ err: e.message }); }
      try { const ss = await api("GET", `/api/workloads/${w.id}/snapshots`); if (alive) setSnaps({ list: ss }); }
      catch (e: any) { if (alive) setSnaps({ err: e.message }); }
    })();
    return () => { alive = false; };
  }, [w.id]);
  let bkBody: ReactNode = <Loading />;
  if (bk && bk.err != null) bkBody = <p style={{ padding: 14 }}>{bk.err}</p>;
  else if (bk && bk.list) bkBody = bk.list.length ? (
    <table><thead><tr><th>When</th><th>Size</th><th>Storage</th><th></th></tr></thead><tbody>
      {bk.list.map((x: any, i: number) => (
        <tr key={x.volid || i}><td>{when(x.when)}</td><td className="mono">{gb(x.size)}</td><td>{x.storage}</td>
          <td style={{ textAlign: "right" }}>{ad ? <button className="btn sm" onClick={() => restore(w.id, x.volid)}>Restore</button> : null}</td></tr>
      ))}</tbody></table>
  ) : <p style={{ padding: 14 }} className="muted">No backups found on the device storage.</p>;
  let snapBody: ReactNode = <Loading />;
  if (snaps && snaps.err != null) snapBody = <p style={{ padding: 14 }}>{snaps.err}</p>;
  else if (snaps && snaps.list) snapBody = snaps.list.length ? (
    <table><thead><tr><th>Name</th><th>Taken</th><th></th></tr></thead><tbody>
      {snaps.list.map((x: any, i: number) => (
        <tr key={x.name || i}><td className="mono">{x.name}<div className="muted" style={{ fontSize: 11.5, fontFamily: "Archivo" }}>{x.desc}</div></td>
          <td>{x.time ? when(new Date(x.time * 1000).toISOString()) : "—"}</td>
          <td style={{ textAlign: "right" }}><div className="btnrow" style={{ justifyContent: "flex-end" }}>
            {op ? <button className="btn sm" onClick={() => Modal.confirm(`Roll back ${w.name} to ${x.name}?`, "Everything changed on the guest since this snapshot is lost.", "Roll back",
              () => fire(job("POST", `/api/workloads/${w.id}/snapshot`, { op: "rollback", name: `${x.name}` })))}>Roll back</button> : null}
            {ad ? <button className="btn sm ghost" onClick={() => fire(job("POST", `/api/workloads/${w.id}/snapshot`, { op: "delete", name: `${x.name}` }))}>Delete</button> : null}
          </div></td></tr>
      ))}</tbody></table>
  ) : <p style={{ padding: 14 }} className="muted">No snapshots.</p>;
  return (
    <div className="grid g2" style={{ marginTop: 14 }}>
      <div className="card"><div className="hd"><h3>Backups on storage</h3></div><div className="bd" id="bk-list" style={{ padding: 0 }}>{bkBody}</div></div>
      <div className="card"><div className="hd"><h3>Snapshots</h3><div className="spacer" />{op ? <button className="btn sm" onClick={() => snapshot(w.id)}>Take snapshot</button> : null}</div>
        <div className="bd" id="snap-list" style={{ padding: 0 }}>{snapBody}</div></div>
    </div>
  );
}

function Backups({ w }: { w: any }) {
  const op = can("Operator"), ad = can("Admin");
  if (w.external) return <div className="empty"><h3>Backups need a Proxmox guest</h3><p>Imported machines are not snapshotted by Proxmox.</p></div>;
  const b = DB.backups.find((x: any) => x.workload === w.id) || DB.backups.find((x: any) => x.node === w.node && !x.workload);
  return (
    <>
      <div className="card"><div className="hd"><h3>Backup schedule</h3><div className="spacer" />
        {op ? <button className="btn sm primary" onClick={() => fire(job("POST", `/api/workloads/${w.id}/backup`, {}))}>Back up now</button> : null}</div><div className="bd">
        {b ? <dl className="kv"><dt>Schedule</dt><dd>{b.schedule} {b.workload ? "" : "(device-wide)"}</dd><dt>Next run</dt><dd>{inFuture(b.next)}</dd>
          <dt>Last backup</dt><dd>{b.last ? when(b.last) : "never"}</dd><dt>Size</dt><dd>{gb(b.size)}</dd><dt>Destination</dt><dd>{b.dest}</dd>
          <dt>Retention</dt><dd>{b.keep}</dd><dt>Verified restore</dt><dd>{b.verified ? "yes, " + when(b.verified_at) : "no"}</dd></dl>
          : <p>No schedule yet for this workload.</p>}
        <div className="btnrow" style={{ marginTop: 14 }}>
          {ad ? <button className="btn" onClick={() => restore(w.id)}>Restore…</button> : null}
          {op ? <button className="btn" onClick={() => addBackup(`w:${w.id}`)}>{b && b.workload ? "Edit schedule" : "Add schedule"}</button> : null}
          {op && b && b.workload ? <button className="btn" onClick={() => fire(job("POST", `/api/backups/${b.id}/verify`))}>Test restore now</button> : null}</div></div></div>
      <StorageLists w={w} />
    </>
  );
}

function Console({ w }: { w: any }) {
  const op = can("Operator");
  const modes: [string, string][] = [];
  if (!w.external && w.type === "lxc") modes.push(["lxc", "Container shell (pct enter)"]);
  if (!w.external && w.type === "vm") modes.push(["vm-serial", "Serial console"]);
  if (!w.external && w.type === "vm" && !w.vga_serial) modes.push(["vnc", "Graphical console (noVNC)"]);
  if (w.ssh_ok) modes.push(["guest", "SSH as " + (w.ssh_user || "")]);
  const cur: string = ui.sub.console && modes.some((m) => m[0] === ui.sub.console) ? ui.sub.console : (modes[0] || [""])[0];
  return (
    <div className="card"><div className="hd"><h3>{cur === "vnc" ? "Graphical console (noVNC)" : "Shell"} · {w.name}</h3><div className="spacer" />
      <div className="pill-toggle">{modes.map((m) => (
        <button key={m[0]} className={m[0] === cur ? "on" : ""} onClick={() => { ui.sub.console = m[0]; rerender(); }}>{m[1]}</button>
      ))}</div>
      <span className="badge acc">session recorded</span></div><div className="bd">
      {!op ? <p>Operators and above can open a console.</p>
        : !modes.length ? <p>No console is available: the machine is not a Proxmox guest and the console has no SSH access.</p>
          : cur === "vnc" ? <VncBox key={"vnc:" + w.id} wid={w.id} /> : <TermBox key={`${cur}:${w.id}`} target={`${cur}:${w.id}`} />}
      {cur === "vm-serial" ? <div className="hint">This VM uses a serial display (typical on ARM). Press Enter to wake the login prompt. Ctrl+O detaches.</div> : null}</div></div>
  );
}

const WL_TABS = ["overview", "containers", "monitoring", "tasks", "backups", "console"];
function WorkloadPage({ param, tab }: PageProps) {
  const w = wl(param);
  if (!w)
    return <div className="empty"><h3>That workload is gone</h3><p>It may have been deleted or moved to the recycle bin.</p><button className="btn" onClick={() => go("workloads")}>Back to workloads</button></div>;
  const t = tab || "overview";
  const n = nodeOf(w);
  const op = can("Operator"), ad = can("Admin");
  let body: ReactNode = null;
  if (t === "overview") body = <Overview w={w} />;
  else if (t === "containers") body = <Containers w={w} />;
  else if (t === "monitoring") body = <MonitoringPanel kind="workload" id={w.id} name={w.name} cpu={w.cpu} ram={w.ram} disk={w.disk} ctrs={DB.containers.filter((c: any) => c.workload === w.id)} />;
  else if (t === "tasks") body = <JobTable list={DB.jobs.filter((j: any) => j.workload === w.id || j.target === w.name)} />;
  else if (t === "backups") body = <Backups w={w} />;
  else body = <Console w={w} />;
  const run = w.status === "running";
  return (
    <>
      <div className="pagehead"><div>
        <div style={{ fontSize: 12.5, color: "var(--ink-3)", marginBottom: 3 }}><a onClick={() => go("workloads")} style={{ cursor: "pointer" }}>Workloads</a> / {n.name}</div>
        <h2>{w.name} <span className={"type-badge type-" + w.type}>{w.external ? "HOST" : String(w.type).toUpperCase()}</span> <Badge s={w.status} /></h2>
        <p>{w.os} · {w.ip} · {siteNameOf(w)}</p></div><div className="spacer" />
        <div className="btnrow">
          {op && !w.external ? <button className="btn" onClick={() => fire(job("POST", `/api/workloads/${w.id}/power`, { action: run ? "stop" : "start" }))}>{run ? "Stop" : "Start"}</button> : null}
          {op && run ? <button className="btn" onClick={() => fire(job("POST", `/api/workloads/${w.id}/power`, { action: "reboot" }))}>Reboot</button> : null}
          {op && !w.external ? <button className="btn" onClick={() => snapshot(w.id)}>Snapshot</button> : null}
          {ad ? <button className="btn danger" onClick={() => deleteItem("workload", w.id)}>Delete</button> : null}
        </div></div>
      <div className="tabs">{WL_TABS.map((x) => (
        <button key={x} className={t === x ? "on" : ""} onClick={() => go("workload", w.id, x)}>{TAB_LABELS[x]}</button>
      ))}</div>
      {body}
    </>
  );
}
PAGES.workload = { crumb: (param) => wl(param)?.name || "Not found", C: WorkloadPage };

/* ---------- monitoring ---------- */
const RANGES = ["Live", "1h", "24h", "7d", "30d"];
let monRange = "24h"; // remembered across pages like the old Mon.range
export function MonitoringPanel({ kind, id, name, cpu, ram, disk, ctrs }:
  { kind: "node" | "workload"; id: any; name: string; cpu: any; ram: any; disk: any; ctrs?: any[] | null }) {
  const [range, setRangeS] = useState(monRange);
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const path = kind === "node" ? `/api/nodes/${id}/rrd` : `/api/workloads/${id}/rrd`;
    const load = async () => {
      try {
        const r = await api("GET", `${path}?tf=${range === "Live" ? "hour" : range}`);
        if (!alive) return;
        setD(r); setErr(null);
      } catch (e: any) { if (!alive) return; setErr(e.message); }
      if (alive && range === "Live") timer = setTimeout(load, 10000);
    };
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [kind, id, range]);
  const setRange = (r: string) => { monRange = r; setRangeS(r); };

  const pick = (a: any): number[] => (range === "Live" ? (a || []).slice(-20) : a || []);
  const last = (a: any): number => (a && a.length ? a[a.length - 1] : 0);
  const loadingBox = <div className="chart-empty" style={{ height: 44 }}>loading…</div>;
  const net = d ? pick(d.netin).map((v, i) => v + (pick(d.netout)[i] || 0)) : [];
  const series: [string, string, any, string][] = [["CPU", "cpu", cpu, "#3B4CCA"], ["Memory", "mem", ram, "#1F8A5B"], ["Disk", "disk", disk, "#B4690E"]];
  return (
    <div className="card"><div className="hd"><h3>{name}</h3><div className="spacer" />
      <div className="btnrow" id="mon-ranges">{RANGES.map((r) => (
        <button key={r} className={"btn sm " + (r === range ? "primary" : "")} onClick={() => setRange(r)}>{r}</button>
      ))}</div></div>
      <div className="bd" id="mon-charts">
        <div className="grid g3" style={{ marginBottom: 18 }}>
          {series.map(([l, k, v, color]) => (
            <div key={k}><div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span>{l}</span><b className="mono" id={`m-${k}-v`}>{d ? last(d[k]) : v}%</b></div>
              <div id={`m-${k}`}>
                {k === "cpu" && err != null ? <div className="chart-empty">{err}</div>
                  : d ? <Sparkline data={pick(d[k])} color={color} h={44} w={400} /> : loadingBox}
              </div></div>
          ))}
        </div>
        <div className="grid g2">
          <div><div className="section-title" style={{ marginTop: 0 }}>Network throughput <span className="muted mono" id="m-net-v">{d ? last(net).toFixed(1) + " KB/s" : ""}</span></div>
            <div id="m-net">{d ? <Sparkline data={net} color="#3B4CCA" h={60} w={400} max={0} /> : null}</div></div>
          <div><div className="section-title" style={{ marginTop: 0 }}>{kind === "node" ? "Disk I/O wait" : "Disk I/O"} <span className="muted mono" id="m-io-v">{d ? last(d.io) + (kind === "node" ? " % iowait" : " MB/s") : ""}</span></div>
            <div id="m-io">{d ? <Sparkline data={pick(d.io)} color="#C03221" h={60} w={400} max={0} /> : null}</div></div>
        </div>
        {ctrs && ctrs.length ? <>
          <div className="section-title">Per-container usage (live)</div>
          <table><thead><tr><th>Container</th><th>CPU</th><th>Memory</th><th>Restarts</th><th>Health</th></tr></thead><tbody>
            {ctrs.map((c: any) => (
              <tr key={c.id}><td>{c.name}</td>
                <td style={{ width: 180 }}><div className="metric"><div className="bar" style={{ flex: 1 }}><i className={lvl(c.cpu)} style={{ width: Math.min(100, c.cpu) + "%" }} /></div><b>{c.cpu}%</b></div></td>
                <td style={{ width: 180 }}><div className="metric"><div className="bar" style={{ flex: 1 }}><i className={lvl(c.ram)} style={{ width: c.ram + "%" }} /></div><b>{c.ram}%</b></div></td>
                <td>{c.restarts}</td><td><Badge s={c.health} /></td></tr>
            ))}</tbody></table>
        </> : null}
      </div></div>
  );
}
